import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLocale } from "../../../i18n/LocaleContext";
import { resolveImage } from "../../../i18n/resolveText";
import { useLocalePath } from "../../../i18n/useLocalePath";
import { heroSlidesActive } from "./heroSlides";
import "./HeroCarousel.css";

const AUTOPLAY_MS = 5000;

export default function HeroCarouselV1() {
  const { t, language } = useLocale();
  const localePath = useLocalePath();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = heroSlidesActive;
  const hasMultipleSlides = slides.length > 1;

  useEffect(() => {
    if (!hasMultipleSlides) return undefined;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [hasMultipleSlides, slides.length]);

  useEffect(() => {
    const first = slides[0];
    if (!first) return undefined;
    const href = first.image || resolveImage(first.images, language);
    if (!href) return undefined;

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = href;
    link.fetchPriority = "high";
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [language, slides]);

  useEffect(() => {
    if (!hasMultipleSlides) return undefined;

    const nextIndex = (currentSlide + 1) % slides.length;
    const prevIndex = (currentSlide - 1 + slides.length) % slides.length;
    [nextIndex, prevIndex].forEach((index) => {
      const slide = slides[index];
      const href = slide?.image || resolveImage(slide?.images, language);
      if (!href) return;
      const img = new Image();
      img.src = href;
    });
    return undefined;
  }, [currentSlide, hasMultipleSlides, language, slides]);

  const getSlideImage = (slide) =>
    slide.image || resolveImage(slide.images, language);

  const goPrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const activeSlide = slides[currentSlide];
  const isFactoryCert = activeSlide.variant === "factory-cert";

  return (
    <section className="hero-carousel-section">
      <div className="hero-carousel-track">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`hero-carousel-slide ${
              currentSlide === index ? "active" : ""
            }`}
            aria-hidden={currentSlide !== index}
          >
            <img
              src={getSlideImage(slide)}
              alt=""
              className="hero-carousel-bg"
              decoding="async"
              fetchPriority={
                index === currentSlide
                  ? "high"
                  : Math.abs(index - currentSlide) === 1 ||
                      (currentSlide === 0 && index === slides.length - 1) ||
                      (currentSlide === slides.length - 1 && index === 0)
                    ? "auto"
                    : "low"
              }
              loading={
                Math.abs(index - currentSlide) <= 1 ||
                (currentSlide === 0 && index === slides.length - 1) ||
                (currentSlide === slides.length - 1 && index === 0)
                  ? "eager"
                  : "lazy"
              }
            />
            {!slide.textInImage && (
              <div
                className={`hero-carousel-overlay${
                  slide.variant === "factory-cert"
                    ? " hero-carousel-overlay--factory"
                    : ""
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {!activeSlide.textInImage && (
        <div
          className={
            isFactoryCert
              ? "hero-carousel-content hero-carousel-content--factory"
              : "container hero-carousel-content"
          }
        >
          {isFactoryCert ? (
            <div className="hero-factory-block">
              <h1 className="hero-factory-title">{t(activeSlide.titleKey)}</h1>
              <p className="hero-factory-metric">
                <span className="hero-factory-metric-prefix">
                  {t(activeSlide.metricPrefixKey)}
                </span>
                <span className="hero-factory-metric-value">
                  {activeSlide.metricValue}
                </span>
                <span className="hero-factory-metric-suffix">
                  {t(activeSlide.metricSuffixKey)}
                </span>
              </p>
              <ul className="hero-factory-features">
                {activeSlide.featureKeys.map((key) => (
                  <li key={key}>{t(key)}</li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              <p className="hero-kicker">{activeSlide.kicker}</p>
              <h1>{activeSlide.title}</h1>
              <p className="hero-desc">{activeSlide.desc}</p>
              <div className="hero-actions">
                <Link to={localePath("/products")} className="btn-primary">
                  {t("viewProduct")}
                </Link>
              </div>
            </>
          )}
        </div>
      )}

      {hasMultipleSlides ? (
        <>
          <button
            className="hero-carousel-arrow left"
            type="button"
            onClick={goPrevSlide}
            aria-label={t("heroSlidePrev")}
          >
            ‹
          </button>
          <button
            className="hero-carousel-arrow right"
            type="button"
            onClick={goNextSlide}
            aria-label={t("heroSlideNext")}
          >
            ›
          </button>

          <div className="hero-carousel-dots">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                className={`hero-carousel-dot ${
                  currentSlide === index ? "active" : ""
                }`}
                onClick={() => setCurrentSlide(index)}
                aria-label={t("heroSlideDot").replace("{n}", String(index + 1))}
              />
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
